import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { format } from 'date-fns'
import HttpClient from '../http-client'
import { httpConfig } from '../constants'
import {
  EtlFileMappings,
  EtlStatus,
  EtlFileCharacteristic,
  EtlQuestion,
  EtlValueMapping,
  EtlFinalCheckError,
} from '../model/models'

export class ETLAPI extends HttpClient {
  public constructor(apiConfig: AxiosRequestConfig) {
    super(apiConfig)
    this.uploadFiles = this.uploadFiles.bind(this)
  }

  public getLatestEtlStatus(): Promise<EtlStatus> {
    const endpoint = `etl/get-latest-etl-status`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public uploadFiles(
    files: File[],
    date: Date,
    runId: number | null,
    isFirstUpload?: boolean,
  ): Promise<any> {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Content-Disposition': `form-data; name="file"`,
      },
    } as AxiosRequestConfig
    const endpoint = `/etl/multi-file-upload`
    const formData = new FormData()
    files.forEach((f) => {
      formData.append(`files`, f)
    })
    formData.append('time_of_import', `${format(date, 'yyyy-MM-dd')}`)
    formData.append('date_effective', `${format(date, 'yyyy-MM-dd')}`)
    if (runId) {
      formData.append('run_id', `${runId}`)
    }
    if (isFirstUpload) {
      formData.append('is_first_upload', `${isFirstUpload}`)
    }

    return this.post(endpoint, formData, config)
      .then((response: AxiosResponse) => response)
      .catch((error) => {
        throw error
      })
  }

  public deleteFile(fileName: string, runId: number): Promise<number> {
    return this.post(`/etl/delete-file/${runId}`, { file_name: fileName })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public downloadFile(fileName: string, runId: number): Promise<number> {
    return this.post(`/etl/download-file/${runId}`, { file_name: fileName })
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public getEtlFileHeaders(runId: number): Promise<Record<string, string[]>> {
    const endpoint = `etl/get-file-headers/${runId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public getFileMappings(runId: number): Promise<any> {
    const endpoint = `etl/map-files/${runId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public setFileMappings(
    runId: number,
    mappings: Record<string, EtlFileMappings>,
  ): Promise<number> {
    const endpoint = `etl/map-files/${runId}`
    const data: any = {}
    Object.keys(mappings).forEach((fileName) => {
      data[fileName] = {
        primary_key: mappings[fileName].primaryKey,
        reference_file: mappings[fileName].referenceFile,
        foreign_key: mappings[fileName].foreignKey,
      }
    })
    return this.post(endpoint, {
      file_info: data,
    })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public setAcceptOrRejectFiles(runId: number, accept: boolean, comment?: string): Promise<number> {
    const endpoint = `etl/accept-files/${runId}`
    return this.post(endpoint, { accept, comment })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public getEtlCharacteristics(runId: number): Promise<any> {
    const endpoint = `etl/characteristics-etl/${runId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public setFileCharacteristics(
    runId: number,
    characteristics: EtlFileCharacteristic[] | null,
    isCompleted: boolean,
  ): Promise<any> {
    const endpoint = `etl/column-mapping/${runId}`
    const params = { characteristics, is_completed: isCompleted }
    return this.post(endpoint, params)
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public processFiles(runId: number): Promise<any> {
    const endpoint = `etl/merge-files/${runId}`
    const params = {}
    return this.post(endpoint, params)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public uploadProcessedFile(file: File, runId: number): Promise<any> {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Content-Disposition': `form-data; name="file"; filename="${file.name}"`,
      },
    } as AxiosRequestConfig
    const endpoint = `/etl/upload-merged-files/${runId}`
    return this.post(endpoint, file, config)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public downloadEtlProcessedFile(runId: number): Promise<any> {
    const endpoint = `etl/get-processed-file/${runId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public addQuestion(runId: number): Promise<any> {
    const endpoint = `etl/create-additional-info/${runId}`
    return this.post(endpoint, {})
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public setQuestions(
    runId: number,
    additionalInfo: EtlQuestion[] | null,
    isCompleted: boolean,
  ): Promise<any> {
    const endpoint = `etl/additional-info/${runId}`
    const params = { additional_info: additionalInfo, is_completed: isCompleted }
    return this.post(endpoint, params)
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public getQuestions(runId: number): Promise<any> {
    const endpoint = `etl/additional-info/${runId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public deleteQuestion(id: number, runId: number | null): Promise<number> {
    return this.post(`/etl/delete-additional-info/${runId}`, { question_id: id })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public getVariables(runId: number): Promise<any> {
    const endpoint = `etl/all-variables/${runId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public getOriginalValues(runId: number, characteristicId: number): Promise<any> {
    const endpoint = `etl/get-original-values/${runId}/${characteristicId}`
    return this.get(endpoint)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public addVariable(runId: number, variable: EtlValueMapping): Promise<EtlValueMapping> {
    return this.post(`/etl/variable/${runId}`, { variable })
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public deleteVariable(runId: number, variableId: number): Promise<number> {
    return this.post(`/etl/delete-variable/${runId}`, { variableId })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public deleteCharVariableGroup(runId: number, characteristicId: number): Promise<number> {
    return this.post(`/etl/delete-variable-for-char/${runId}`, { characteristicId })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public updateEtlStep(runId: number | null, step: string): Promise<EtlStatus> {
    return this.post(`/etl/update-etl-step/${runId}`, { step })
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public goBackStep(runId: number | null): Promise<EtlStatus> {
    return this.post(`/etl/go-back-step/${runId}`, {})
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public getEtlFinalCheckErrors(runId: number, reported: number): Promise<EtlFinalCheckError[]> {
    return this.get(`/etl/final-check/${runId}/${reported}`)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        throw error
      })
  }

  public setEtlFinalCheckErrors(runId: number, errors: EtlFinalCheckError[]): Promise<number> {
    return this.post(`/etl/final-check/${runId}`, { errors })
      .then((response: AxiosResponse) => response.status)
      .catch((error) => {
        throw error
      })
  }

  public uploadFixturesFiles(files: File[], runId: number): Promise<any> {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Content-Disposition': `form-data; name="file"`,
      },
    } as AxiosRequestConfig
    const endpoint = `/etl/upload-fixtures/${runId}`
    const formData = new FormData()
    files.forEach((f) => {
      formData.append(`files`, f)
    })

    return this.post(endpoint, formData, config)
      .then((response: AxiosResponse) => response)
      .catch((error) => {
        throw error
      })
  }
}

export const etlAPI = new ETLAPI(httpConfig)
